import { useCallback, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

/**
 * A piece of view state — a table filter, a settings tab — kept in the query string.
 *
 * The URL mirrors what you are looking at; the history records where you have *been*. Narrowing a
 * table is not going somewhere, so changing a filter replaces the current entry rather than adding
 * one. What that buys is the round trip that actually matters: filter the events list, open an
 * event, press Back, and you land on the list still filtered — because the filtered URL is the
 * entry you left from. Pushing would buy the ability to step backwards through each filter change
 * individually, at the price of needing eight Backs to leave a page you narrowed eight times.
 *
 * `push` is available for the rare caller that genuinely moves somewhere.
 *
 * Three further things the hand-rolled versions got wrong, all fixed here:
 *
 * - **Sibling parameters.** `setParams({ device: id })` replaces the *whole* query string, so
 *   setting one filter silently dropped every other. Harmless while a page had one filter, wrong
 *   the moment it has two — which is the case as soon as tabs join them. Updates merge.
 *
 * - **Default values in the URL.** A param is dropped when it equals the default, so the tidy URL
 *   is the shareable one and `?tab=setup&filter=all` never accumulates.
 *
 * - **Router state.** `setSearchParams` navigates, and a navigation with no state clears the
 *   entry's state. That silently discarded the back link a page was opened with — switch tab on a
 *   truck reached from an incident report and the way back to the report was gone. Whatever state
 *   the current entry carries is carried forward.
 */

/**
 * `parse` returns a *box* rather than the value itself, so "this parsed to undefined" and "this is
 * not a value I recognise" stay distinguishable. That matters wherever undefined is a real choice
 * and not the default — the Events range filter defaults to 24 hours and offers "All time" as
 * undefined, and a bare `T | undefined` return collapses the two, silently snapping All time back
 * to 24 hours on every reload.
 */
export type UrlCodec<T> = {
    parse: (raw: string) => { value: T } | undefined;
    /** Return undefined to leave the parameter out of the URL entirely. */
    format: (value: T) => string | undefined;
};

export type SetUrlState<T> = (next: T, options?: { push?: boolean }) => void;

export function useUrlState<T>(key: string, defaultValue: T, codec: UrlCodec<T>): [T, SetUrlState<T>] {
    const [params, setParams] = useSearchParams();
    // Through a ref so the setter keeps its identity across navigations — several callers list it
    // in an effect's dependencies.
    const stateRef = useRef<unknown>();
    stateRef.current = useLocation().state;

    const raw = params.get(key);
    const parsed = raw === null ? undefined : codec.parse(raw);
    // An unparseable value reads as the default rather than throwing: query strings are typed by
    // hand and pasted between people, and a stale link should degrade to the default view.
    const value = parsed === undefined ? defaultValue : parsed.value;

    const set = useCallback<SetUrlState<T>>(
        (next, options) => {
            setParams(
                (prev) => {
                    const merged = new URLSearchParams(prev);
                    const formatted = codec.format(next);
                    if (formatted === undefined || formatted === codec.format(defaultValue)) merged.delete(key);
                    else merged.set(key, formatted);
                    return merged;
                },
                // Replacing keeps the history a record of pages visited, not of every filter tweak.
                // The state rides along, or narrowing a table would throw away how you got here.
                { replace: !options?.push, state: stateRef.current },
            );
        },
        // Codecs are module-level constants, so this stays stable across renders.
        [key, defaultValue, codec, setParams],
    );

    return [value, set];
}

/** A string union — tab keys, filter keys. */
export function urlEnum<T extends string>(allowed: readonly T[]): UrlCodec<T> {
    return {
        parse: (raw) => (allowed.includes(raw as T) ? { value: raw as T } : undefined),
        format: (value) => value,
    };
}

/** A free-form id, where the absence of the parameter means "no selection". */
export const urlOptionalId: UrlCodec<string | undefined> = {
    parse: (raw) => ({ value: raw || undefined }),
    format: (value) => value || undefined,
};

/** A number whose "none" is the default — an unfiltered table, written as an absent parameter. */
export const urlOptionalNumber: UrlCodec<number | undefined> = {
    parse: (raw) => {
        const n = Number(raw);
        return Number.isFinite(n) ? { value: n } : undefined;
    },
    format: (value) => (value === undefined ? undefined : String(value)),
};

/**
 * A number where "none" is a deliberate choice rather than the default — "All time" on a range
 * filter that defaults to 24 hours. It needs its own token so it can be written down: omitting the
 * parameter would read back as the default and quietly re-narrow the view on every reload.
 */
export const urlNumberOrAll: UrlCodec<number | undefined> = {
    parse: (raw) => {
        if (raw === "all") return { value: undefined };
        const n = Number(raw);
        return Number.isFinite(n) ? { value: n } : undefined;
    },
    format: (value) => (value === undefined ? "all" : String(value)),
};

/** A flag. Only the non-default state appears, so URLs stay short. */
export const urlFlag: UrlCodec<boolean> = {
    parse: (raw) => ({ value: raw === "1" || raw === "true" }),
    format: (value) => (value ? "1" : undefined),
};
